/* eslint-disable camelcase */
import {
    AD_CONVERSION_ID,
    ENV,
    SIGN_UP_CONVERSION,
    SIGN_UP_FIGMA_DESIGNER_CONVERSION,
    ADVANCED_PURCHASE_CONVERSION,
    ENT_INQUORY_CONVERSION,
    LINKEDIN_CONVERSION_ID,
    LINKEDIN_FIGMA_DESIGNER_CONVERSION_ID,
    TWITTER_CONVERSION_ID,
    TIKTOK_PIXEL_ID,
    REDDIT_PIXEL_ID
} from "../constants";
import { WorkspacePlanType, EXEC_ENV } from "../enums";

import { shouldTrackUser } from "./user";

const sendGAPageView = withExceptionHandler(({ title, source }: { title?: string; source?: string; } = {}) => {
    if (!Zeplin.user || shouldTrackUser(Zeplin.user)) {
        gtag("set", {
            location: location.href
        });

        if (title) {
            gtag("set", {
                title
            });
        }

        gtag("event", "page_view", source && {
            page_path: source
        });
    }
});

function shouldSendToFBPixel(): boolean {
    return ENV === EXEC_ENV.production || ENV === EXEC_ENV.beta;
}

function shouldSendMutiny(): boolean {
    return ENV === EXEC_ENV.production || ENV === EXEC_ENV.beta;
}

const sendVisitSignup = withExceptionHandler(() => {
    if (!Zeplin.user || shouldTrackUser(Zeplin.user)) {
        shouldSendToFBPixel() && fbq("trackCustom", "StartRegistration");
    }
});

const sendEnterpriseInquiry = withExceptionHandler(() => {
    if (!Zeplin.user || shouldTrackUser(Zeplin.user)) {
        gtag("event", "ent_inquiry", {
            event_category: "monetization",
            event_label: "Enterprise inquiry"
        });
        gtag("event", "conversion", { send_to: `${AD_CONVERSION_ID}/${ENT_INQUORY_CONVERSION}` });
        shouldSendToFBPixel() && fbq("track", "Contact");
    }
});

enum ConversionType {
    advanced = "advanced_conversion"
}

enum ConversionLabel {
    advanced = "Advanced conversion"
}

const sendWorkspaceConversion = withExceptionHandler((planToUpgrade: WorkspacePlanType) => {
    if (!Zeplin.user || shouldTrackUser(Zeplin.user)) {
        if (planToUpgrade === WorkspacePlanType.ADVANCED) {
            gtag("event", ConversionType.advanced, {
                event_category: "monetization",
                event_label: ConversionLabel.advanced
            });
            gtag("event", "conversion", { send_to: `${AD_CONVERSION_ID}/${ADVANCED_PURCHASE_CONVERSION}` });
            shouldSendToFBPixel() && fbq("track", "Purchase", { content_name: ConversionLabel.advanced });
            if (REDDIT_PIXEL_ID) {
                rdt("track", "Purchase");
            }
        }
    }
});

const sendSignup = withExceptionHandler(() => {
    if (!Zeplin.user || shouldTrackUser(Zeplin.user)) {
        gtag("event", "sign_up", {
            event_category: "onboarding",
            event_label: "Signed up"
        });
        gtag("event", "conversion", { send_to: `${AD_CONVERSION_ID}/${SIGN_UP_CONVERSION}` });
        shouldSendToFBPixel() && fbq("track", "CompleteRegistration");
        shouldSendMutiny() && mutiny.client.trackConversion({
            name: "zeplin-sign-up"
        });

        const linkedinConversionId = parseInt(LINKEDIN_CONVERSION_ID, 10);

        if (linkedinConversionId) {
            lintrk("track", { conversion_id: linkedinConversionId });
        }

        if (TWITTER_CONVERSION_ID) {
            twttr.conversion.trackPid(TWITTER_CONVERSION_ID, { tw_sale_amount: 0, tw_order_quantity: 0 });
        }

        if (TIKTOK_PIXEL_ID) {
            ttq.instance(TIKTOK_PIXEL_ID).track("CompleteRegistration");
        }

        if (REDDIT_PIXEL_ID) {
            rdt("track", "SignUp");
        }
    }
});

const sendSignupFigmaDesigner = withExceptionHandler(() => {
    if (!Zeplin.user || shouldTrackUser(Zeplin.user)) {
        gtag("event", "conversion", { send_to: `${AD_CONVERSION_ID}/${SIGN_UP_FIGMA_DESIGNER_CONVERSION}` });

        const linkedinFigmaDesignerConversionId = parseInt(LINKEDIN_FIGMA_DESIGNER_CONVERSION_ID, 10);

        if (linkedinFigmaDesignerConversionId) {
            lintrk("track", { conversion_id: linkedinFigmaDesignerConversionId });
        }

        shouldSendToFBPixel() && fbq("trackCustom", "FigmaDesigner");
    }
});

function withExceptionHandler<A extends unknown[]>(func: (...args: A)=> void) {
    return (...args: A) => {
        try {
            // Some ad-blockers like uBlock Origin also blocks trackers. If we do not handle the error here when it blocks any
            // of the trackers above, it might cause side effects like be handled somewhere else by mistake or never handled at all.
            func(...args);
        } catch (e) {
            // No-op
        }
    };
}

function trackOncePerDay(eventName: string, props = {}) {
    const key = `mp_last_${eventName}${JSON.stringify(props)}_timestamp`;
    const last = localStorage.getItem(key);
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    if (last && now - Number(last) < oneDay) {
        return;
    }

    localStorage.setItem(key, now.toString());

    mixpanel.master.track(eventName, props);
}

export {
    sendGAPageView,
    sendVisitSignup,
    sendEnterpriseInquiry,
    sendWorkspaceConversion,
    sendSignup,
    sendSignupFigmaDesigner,
    trackOncePerDay
};
